/**
 * Currency Configuration
 *
 * Defines supported currencies with their display properties and Plaid availability.
 * Adding new currencies is simple - just add a new entry to CURRENCIES.
 */

// Plaid-supported country codes for each currency
// Note: Not all currencies have Plaid bank sync support
const PLAID_COUNTRIES = {
  USD: ['US'],
  CAD: ['CA'],
  GBP: ['GB'],
  EUR: ['IE', 'DE', 'ES', 'FR', 'NL'],
};

/**
 * Currency definitions
 *
 * Each currency has:
 * - code: ISO 4217 currency code
 * - symbol: Display symbol (used for short format)
 * - locale: Primary locale for number formatting
 * - name: Full currency name
 * - plaidCountries: Array of Plaid-supported country codes (empty = manual-only)
 * - symbolPosition: 'before' or 'after' the number
 * - searchTerms: Array of country/region names for search matching
 */
export const CURRENCIES = {
  // Plaid-supported currencies (bank sync available)
  USD: {
    code: 'USD',
    symbol: '$',
    locale: 'en-US',
    name: 'US Dollar',
    plaidCountries: PLAID_COUNTRIES.USD,
    symbolPosition: 'before',
    searchTerms: ['United States', 'America', 'USA'],
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    locale: 'en-CA',
    name: 'Canadian Dollar',
    plaidCountries: PLAID_COUNTRIES.CAD,
    symbolPosition: 'before',
    searchTerms: ['Canada'],
  },
  GBP: {
    code: 'GBP',
    symbol: '\u00a3',
    locale: 'en-GB',
    name: 'British Pound',
    plaidCountries: PLAID_COUNTRIES.GBP,
    symbolPosition: 'before',
    searchTerms: ['United Kingdom', 'UK', 'England', 'Britain'],
  },
  EUR: {
    code: 'EUR',
    symbol: '\u20ac',
    locale: 'de-DE',
    name: 'Euro',
    plaidCountries: PLAID_COUNTRIES.EUR,
    symbolPosition: 'before',
    searchTerms: ['Europe', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 'Ireland', 'Austria', 'Belgium', 'Finland', 'Portugal', 'Greece'],
  },

  // Manual-only currencies (no bank sync, but full app functionality)
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    locale: 'en-AU',
    name: 'Australian Dollar',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Australia'],
  },
  NZD: {
    code: 'NZD',
    symbol: 'NZ$',
    locale: 'en-NZ',
    name: 'New Zealand Dollar',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['New Zealand'],
  },
  PHP: {
    code: 'PHP',
    symbol: '\u20b1',
    locale: 'en-PH',
    name: 'Philippine Peso',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Philippines', 'Manila'],
  },
  AED: {
    code: 'AED',
    symbol: '\u062f.\u0625',
    locale: 'ar-AE',
    name: 'UAE Dirham',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['United Arab Emirates', 'Dubai', 'Abu Dhabi'],
  },
  SAR: {
    code: 'SAR',
    symbol: '\ufdfc',
    locale: 'ar-SA',
    name: 'Saudi Riyal',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Saudi Arabia', 'Riyadh'],
  },
  INR: {
    code: 'INR',
    symbol: '\u20b9',
    locale: 'en-IN',
    name: 'Indian Rupee',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['India', 'Mumbai', 'Delhi'],
  },
  MXN: {
    code: 'MXN',
    symbol: 'MX$',
    locale: 'es-MX',
    name: 'Mexican Peso',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Mexico'],
  },
  SGD: {
    code: 'SGD',
    symbol: 'S$',
    locale: 'en-SG',
    name: 'Singapore Dollar',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Singapore'],
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    locale: 'pt-BR',
    name: 'Brazilian Real',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Brazil'],
  },
  ARS: {
    code: 'ARS',
    symbol: 'AR$',
    locale: 'es-AR',
    name: 'Argentine Peso',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Argentina', 'Buenos Aires'],
  },
  IDR: {
    code: 'IDR',
    symbol: 'Rp',
    locale: 'id-ID',
    name: 'Indonesian Rupiah',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Indonesia', 'Jakarta'],
  },
  LYD: {
    code: 'LYD',
    symbol: '\u0644.\u062f',
    locale: 'ar-LY',
    name: 'Libyan Dinar',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Libya', 'Tripoli'],
  },
  NPR: {
    code: 'NPR',
    symbol: '\u0930\u0942',
    locale: 'ne-NP',
    name: 'Nepalese Rupee',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Nepal', 'Kathmandu'],
  },
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    locale: 'en-ZA',
    name: 'South African Rand',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['South Africa', 'Johannesburg', 'Cape Town'],
  },
  JPY: {
    code: 'JPY',
    symbol: '\u00a5',
    locale: 'ja-JP',
    name: 'Japanese Yen',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Japan', 'Tokyo'],
  },
  KRW: {
    code: 'KRW',
    symbol: '\u20a9',
    locale: 'ko-KR',
    name: 'South Korean Won',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['South Korea', 'Korea', 'Seoul'],
  },
  THB: {
    code: 'THB',
    symbol: '\u0e3f',
    locale: 'th-TH',
    name: 'Thai Baht',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Thailand', 'Bangkok'],
  },
  TRY: {
    code: 'TRY',
    symbol: '\u20ba',
    locale: 'tr-TR',
    name: 'Turkish Lira',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Turkey', 'Istanbul', 'Ankara'],
  },
  NGN: {
    code: 'NGN',
    symbol: '\u20a6',
    locale: 'en-NG',
    name: 'Nigerian Naira',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Nigeria', 'Lagos', 'Abuja'],
  },
  KES: {
    code: 'KES',
    symbol: 'KSh',
    locale: 'en-KE',
    name: 'Kenyan Shilling',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Kenya', 'Nairobi'],
  },
  EGP: {
    code: 'EGP',
    symbol: 'E\u00a3',
    locale: 'ar-EG',
    name: 'Egyptian Pound',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Egypt', 'Cairo'],
  },
  PKR: {
    code: 'PKR',
    symbol: '\u20a8',
    locale: 'ur-PK',
    name: 'Pakistani Rupee',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Pakistan', 'Karachi', 'Islamabad'],
  },
  BDT: {
    code: 'BDT',
    symbol: '\u09f3',
    locale: 'bn-BD',
    name: 'Bangladeshi Taka',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Bangladesh', 'Dhaka'],
  },
  VND: {
    code: 'VND',
    symbol: '\u20ab',
    locale: 'vi-VN',
    name: 'Vietnamese Dong',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Vietnam', 'Ho Chi Minh', 'Hanoi'],
  },
  TWD: {
    code: 'TWD',
    symbol: 'NT$',
    locale: 'zh-TW',
    name: 'New Taiwan Dollar',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Taiwan', 'Taipei'],
  },
  HKD: {
    code: 'HKD',
    symbol: 'HK$',
    locale: 'zh-HK',
    name: 'Hong Kong Dollar',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Hong Kong'],
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    locale: 'de-CH',
    name: 'Swiss Franc',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Switzerland', 'Zurich', 'Geneva'],
  },
  SEK: {
    code: 'SEK',
    symbol: 'kr',
    locale: 'sv-SE',
    name: 'Swedish Krona',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Sweden', 'Stockholm'],
  },
  NOK: {
    code: 'NOK',
    symbol: 'kr',
    locale: 'nb-NO',
    name: 'Norwegian Krone',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Norway', 'Oslo'],
  },
  DKK: {
    code: 'DKK',
    symbol: 'kr',
    locale: 'da-DK',
    name: 'Danish Krone',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Denmark', 'Copenhagen'],
  },
  PLN: {
    code: 'PLN',
    symbol: 'z\u0142',
    locale: 'pl-PL',
    name: 'Polish Zloty',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Poland', 'Warsaw'],
  },
  CZK: {
    code: 'CZK',
    symbol: 'K\u010d',
    locale: 'cs-CZ',
    name: 'Czech Koruna',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Czech Republic', 'Czechia', 'Prague'],
  },
  HUF: {
    code: 'HUF',
    symbol: 'Ft',
    locale: 'hu-HU',
    name: 'Hungarian Forint',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Hungary', 'Budapest'],
  },
  CLP: {
    code: 'CLP',
    symbol: 'CL$',
    locale: 'es-CL',
    name: 'Chilean Peso',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Chile', 'Santiago'],
  },
  COP: {
    code: 'COP',
    symbol: 'CO$',
    locale: 'es-CO',
    name: 'Colombian Peso',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Colombia', 'Bogota'],
  },
  PEN: {
    code: 'PEN',
    symbol: 'S/',
    locale: 'es-PE',
    name: 'Peruvian Sol',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Peru', 'Lima'],
  },
  GHS: {
    code: 'GHS',
    symbol: 'GH\u20b5',
    locale: 'en-GH',
    name: 'Ghanaian Cedi',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Ghana', 'Accra'],
  },
  MYR: {
    code: 'MYR',
    symbol: 'RM',
    locale: 'ms-MY',
    name: 'Malaysian Ringgit',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Malaysia', 'Kuala Lumpur'],
  },
  OMR: {
    code: 'OMR',
    symbol: 'ر.ع.',
    locale: 'ar-OM',
    name: 'Omani Rial',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Oman', 'Muscat'],
  },
  DOP: {
    code: 'DOP',
    symbol: 'RD$',
    locale: 'es-DO',
    name: 'Dominican Peso',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Dominican Republic', 'Santo Domingo'],
  },
  TTD: {
    code: 'TTD',
    symbol: 'TT$',
    locale: 'en-TT',
    name: 'Trinidad & Tobago Dollar',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Trinidad', 'Tobago', 'Trinidad and Tobago', 'Port of Spain'],
  },
  QAR: {
    code: 'QAR',
    symbol: 'ر.ق',
    locale: 'ar-QA',
    name: 'Qatari Riyal',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Qatar', 'Doha'],
  },
  UYU: {
    code: 'UYU',
    symbol: '$U',
    locale: 'es-UY',
    name: 'Uruguayan Peso',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Uruguay', 'Montevideo'],
  },
  GEL: {
    code: 'GEL',
    symbol: '₾',
    locale: 'ka-GE',
    name: 'Georgian Lari',
    plaidCountries: [],
    symbolPosition: 'before',
    searchTerms: ['Georgia', 'Tbilisi'],
  },
};

// Default currency for new users (and migration fallback)
export const DEFAULT_CURRENCY = 'USD';

/**
 * Map IANA timezone to most likely currency code.
 * Uses prefix matching for regions with a single currency (e.g. Australia/*).
 * @param {string} timezone - IANA timezone string (e.g. 'America/New_York')
 * @returns {string} ISO currency code
 */
export const getCurrencyFromTimezone = (timezone) => {
  if (!timezone) return DEFAULT_CURRENCY;

  const TIMEZONE_MAP = {
    // USD - United States
    'America/New_York': 'USD',
    'America/Chicago': 'USD',
    'America/Denver': 'USD',
    'America/Los_Angeles': 'USD',
    'America/Phoenix': 'USD',
    'America/Anchorage': 'USD',
    'Pacific/Honolulu': 'USD',
    'America/Detroit': 'USD',
    'America/Indiana/Indianapolis': 'USD',
    'America/Boise': 'USD',
    // CAD - Canada
    'America/Toronto': 'CAD',
    'America/Vancouver': 'CAD',
    'America/Edmonton': 'CAD',
    'America/Winnipeg': 'CAD',
    'America/Halifax': 'CAD',
    'America/St_Johns': 'CAD',
    'America/Regina': 'CAD',
    // GBP - United Kingdom
    'Europe/London': 'GBP',
    // EUR - Eurozone
    'Europe/Berlin': 'EUR',
    'Europe/Paris': 'EUR',
    'Europe/Madrid': 'EUR',
    'Europe/Rome': 'EUR',
    'Europe/Amsterdam': 'EUR',
    'Europe/Brussels': 'EUR',
    'Europe/Vienna': 'EUR',
    'Europe/Dublin': 'EUR',
    'Europe/Helsinki': 'EUR',
    'Europe/Lisbon': 'EUR',
    'Europe/Athens': 'EUR',
    // NZD - New Zealand
    'Pacific/Auckland': 'NZD',
    // PHP - Philippines
    'Asia/Manila': 'PHP',
    // AED - UAE
    'Asia/Dubai': 'AED',
    // SAR - Saudi Arabia
    'Asia/Riyadh': 'SAR',
    // INR - India
    'Asia/Kolkata': 'INR',
    'Asia/Calcutta': 'INR',
    // MXN - Mexico
    'America/Mexico_City': 'MXN',
    'America/Cancun': 'MXN',
    'America/Monterrey': 'MXN',
    'America/Tijuana': 'MXN',
    // SGD - Singapore
    'Asia/Singapore': 'SGD',
    // BRL - Brazil
    'America/Sao_Paulo': 'BRL',
    'America/Fortaleza': 'BRL',
    'America/Recife': 'BRL',
    'America/Bahia': 'BRL',
    'America/Manaus': 'BRL',
    'America/Belem': 'BRL',
    'America/Cuiaba': 'BRL',
    // ARS - Argentina
    'America/Argentina/Buenos_Aires': 'ARS',
    'America/Argentina/Cordoba': 'ARS',
    'America/Argentina/Mendoza': 'ARS',
    'America/Argentina/Tucuman': 'ARS',
    'America/Argentina/Salta': 'ARS',
    'America/Argentina/Ushuaia': 'ARS',
    // IDR - Indonesia
    'Asia/Jakarta': 'IDR',
    'Asia/Makassar': 'IDR',
    'Asia/Jayapura': 'IDR',
    'Asia/Pontianak': 'IDR',
    // LYD - Libya
    'Africa/Tripoli': 'LYD',
    // NPR - Nepal
    'Asia/Kathmandu': 'NPR',
    // ZAR - South Africa
    'Africa/Johannesburg': 'ZAR',
    // JPY - Japan
    'Asia/Tokyo': 'JPY',
    // KRW - South Korea
    'Asia/Seoul': 'KRW',
    // THB - Thailand
    'Asia/Bangkok': 'THB',
    // TRY - Turkey
    'Europe/Istanbul': 'TRY',
    // NGN - Nigeria
    'Africa/Lagos': 'NGN',
    // KES - Kenya
    'Africa/Nairobi': 'KES',
    // EGP - Egypt
    'Africa/Cairo': 'EGP',
    // PKR - Pakistan
    'Asia/Karachi': 'PKR',
    // BDT - Bangladesh
    'Asia/Dhaka': 'BDT',
    // VND - Vietnam
    'Asia/Ho_Chi_Minh': 'VND',
    'Asia/Saigon': 'VND',
    // TWD - Taiwan
    'Asia/Taipei': 'TWD',
    // HKD - Hong Kong
    'Asia/Hong_Kong': 'HKD',
    // CHF - Switzerland
    'Europe/Zurich': 'CHF',
    // SEK - Sweden
    'Europe/Stockholm': 'SEK',
    // NOK - Norway
    'Europe/Oslo': 'NOK',
    // DKK - Denmark
    'Europe/Copenhagen': 'DKK',
    // PLN - Poland
    'Europe/Warsaw': 'PLN',
    // CZK - Czech Republic
    'Europe/Prague': 'CZK',
    // HUF - Hungary
    'Europe/Budapest': 'HUF',
    // CLP - Chile
    'America/Santiago': 'CLP',
    // COP - Colombia
    'America/Bogota': 'COP',
    // PEN - Peru
    'America/Lima': 'PEN',
    // GHS - Ghana
    'Africa/Accra': 'GHS',
    // MYR - Malaysia
    'Asia/Kuala_Lumpur': 'MYR',
    'Asia/Kuching': 'MYR',
    // OMR - Oman
    'Asia/Muscat': 'OMR',
    // DOP - Dominican Republic
    'America/Santo_Domingo': 'DOP',
    // TTD - Trinidad and Tobago
    'America/Port_of_Spain': 'TTD',
    // QAR - Qatar
    'Asia/Qatar': 'QAR',
    // UYU - Uruguay
    'America/Montevideo': 'UYU',
    // GEL - Georgia
    'Asia/Tbilisi': 'GEL',
  };

  // Direct match
  if (TIMEZONE_MAP[timezone]) return TIMEZONE_MAP[timezone];

  // Prefix match for Australia
  if (timezone.startsWith('Australia/')) return 'AUD';

  return DEFAULT_CURRENCY;
};

/**
 * Get currency configuration by code
 * @param {string} code - ISO currency code (e.g., 'USD', 'GBP')
 * @returns {object} Currency config or USD as fallback
 */
export const getCurrency = (code) => {
  return CURRENCIES[code] || CURRENCIES[DEFAULT_CURRENCY];
};

/**
 * Get region-appropriate placeholder names for IOU/debt forms based on currency.
 * Returns a string like "e.g., Priya, Mom, Arun" using names common in that currency's region.
 * @param {string} code - ISO currency code
 * @returns {string} Placeholder string
 */
export const getPersonPlaceholder = (code) => {
  const REGIONAL_NAMES = {
    USD: 'Marcus, Mom, Sarah',
    CAD: 'Liam, Mom, Priya',
    GBP: 'James, Mum, Aisha',
    EUR: 'Lukas, Mama, Sofia',
    AUD: 'Jack, Mum, Aroha',
    NZD: 'Tane, Mum, Aroha',
    PHP: 'Jomar, Nanay, Maria',
    AED: 'Ahmed, Mama, Fatima',
    SAR: 'Khalid, Mama, Noura',
    INR: 'Arjun, Amma, Priya',
    MXN: 'Carlos, Mamá, Lucía',
    SGD: 'Wei, Mom, Priya',
    BRL: 'Lucas, Mãe, Ana',
    ARS: 'Mateo, Mamá, Camila',
    IDR: 'Budi, Ibu, Sari',
    LYD: 'Omar, Mama, Salma',
    NPR: 'Arun, Aamaa, Sita',
    ZAR: 'Thabo, Mom, Naledi',
    JPY: 'Yuto, Okaa-san, Sakura',
    KRW: 'Minjun, Eomma, Jisoo',
    THB: 'Somchai, Mae, Ploy',
    TRY: 'Emre, Anne, Elif',
    NGN: 'Chidi, Mama, Amina',
    KES: 'Brian, Mama, Wanjiku',
    EGP: 'Ahmed, Mama, Nour',
    PKR: 'Ali, Ammi, Ayesha',
    BDT: 'Rahim, Amma, Fatima',
    VND: 'Minh, Mẹ, Linh',
    TWD: 'Wei, Mama, Mei',
    HKD: 'Ka Ming, Mom, Yan',
    CHF: 'Lukas, Mama, Elena',
    SEK: 'Erik, Mamma, Astrid',
    NOK: 'Lars, Mamma, Ingrid',
    DKK: 'Magnus, Mor, Freja',
    PLN: 'Kacper, Mama, Zofia',
    CZK: 'Jakub, Máma, Tereza',
    HUF: 'Bence, Anya, Réka',
    CLP: 'Matías, Mamá, Catalina',
    COP: 'Andrés, Mamá, Valentina',
    PEN: 'Diego, Mamá, Lucía',
    GHS: 'Kwame, Maame, Ama',
    MYR: 'Haziq, Mak, Nurul',
    OMR: 'Said, Mama, Maryam',
    DOP: 'Juan, Mamá, María',
    TTD: 'Keron, Mom, Keisha',
    QAR: 'Hamad, Mama, Noura',
    UYU: 'Martín, Mamá, Valentina',
    GEL: 'Giorgi, Deda, Nino',
  };
  const names = REGIONAL_NAMES[code] || REGIONAL_NAMES.USD;
  return `e.g., ${names}`;
};

/**
 * Check if a currency supports Plaid bank sync
 * @param {string} code - ISO currency code
 * @returns {boolean}
 */
export const hasBankSyncSupport = (code) => {
  const currency = getCurrency(code);
  return currency.plaidCountries && currency.plaidCountries.length > 0;
};

/**
 * Get Plaid country codes for a currency
 * @param {string} code - ISO currency code
 * @returns {string[]} Array of country codes
 */
export const getPlaidCountries = (code) => {
  const currency = getCurrency(code);
  return currency.plaidCountries || [];
};

/**
 * Get all supported currency codes
 * @returns {string[]} Array of currency codes
 */
export const getSupportedCurrencies = () => {
  return Object.keys(CURRENCIES);
};

/**
 * Get currencies grouped by Plaid support
 * @returns {{ plaidSupported: object[], manualOnly: object[] }}
 */
export const getCurrenciesBySupport = () => {
  const all = Object.values(CURRENCIES);
  return {
    plaidSupported: all.filter(c => c.plaidCountries && c.plaidCountries.length > 0),
    manualOnly: all.filter(c => !c.plaidCountries || c.plaidCountries.length === 0),
  };
};

/**
 * Search/filter currencies by query string.
 * Matches against name, code, symbol, and searchTerms.
 * @param {string} query - Search string
 * @returns {object[]} Matching currency objects
 */
export const searchCurrencies = (query) => {
  if (!query || !query.trim()) return Object.values(CURRENCIES);
  const q = query.trim().toLowerCase();
  return Object.values(CURRENCIES).filter(c => {
    if (c.name.toLowerCase().includes(q)) return true;
    if (c.code.toLowerCase().includes(q)) return true;
    if (c.symbol.toLowerCase().includes(q)) return true;
    if (c.searchTerms && c.searchTerms.some(t => t.toLowerCase().includes(q))) return true;
    return false;
  });
};
